import React, { useState, useEffect } from "react";
import apiClient, { getApiError } from "../apiClient";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useShop } from "../context/ShopContext";
import { FaSpinner } from "react-icons/fa";
import EmptyState from "./EmptyState";
import "./parchaTable.css";
import { fetchSortOrderMap, applySortOrder, buildCategorySegments } from "../utils/sortOrder";

/* ─── Brand colours ──────────────────────────── */
const P   = "#7B1D4F";
const PDK = "#5E1540";
const GRN = "#16A34A";
const ORG = "#EA580C";
const RED = "#DC2626";
const BDR = "#E5E7EB";
const MUT = "#6B7280";
const fmt = n => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const pad      = n => String(n).padStart(2, "0");
const nowD     = new Date();
const timeStr  = nowD.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" });
const todayISO = `${nowD.getFullYear()}-${pad(nowD.getMonth()+1)}-${pad(nowD.getDate())}`;

const prevDay = d => {
  const dt = new Date(d); dt.setDate(dt.getDate() - 1);
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
};

/* ═══════════════════════════════════════════════════
   Previous English Parcha Entry
   typeId 1 — Normal + Reverse Historical modes
═══════════════════════════════════════════════════ */
export default function StockReverse() {
  const navigate = useNavigate();
  const { userId, companyId } = useShop();

  const [shopId,      setShopId]  = useState(null);
  const [tokenString, setToken]   = useState(null);
  const [shopName,    setShopName]= useState(null);

  /* ── mode ── */
  const [mode, setMode] = useState("normal"); // "normal" | "reverse"

  /* ══════════ NORMAL MODE state ══════════ */
  const [apiConditionMet, setApiConditionMet] = useState(null);
  const [rows,            setRows]            = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [date,            setDate]            = useState(todayISO);
  const [search,          setSearch]          = useState("");
  const [activeCat,       setActiveCat]       = useState("ALL");
  const [preview,         setPreview]         = useState(false);
  const [selectedVars,    setSelectedVars]    = useState({});
  const [popupData1,      setPopupData1]      = useState([]);
  const [popupData2,      setPopupData2]      = useState([]);

  /* ══════════ REVERSE MODE state ══════════ */
  const [baseDate,           setBaseDate]          = useState(todayISO);
  const [revDate,            setRevDate]            = useState(prevDay(todayISO));
  const [revRows,            setRevRows]            = useState([]);
  const [revSelVars,         setRevSelVars]         = useState({});
  const [revLoading,         setRevLoading]         = useState(false);
  const [revPreview,         setRevPreview]         = useState(false);
  const [revApiConditionMet, setRevApiConditionMet] = useState(null);
  const [revSearch,          setRevSearch]          = useState("");

  /* ── Daily Closing Summary (local UI state only) ── */
  const [commission, setCommission] = useState("");
  const [phonePe,    setPhonePe]    = useState("");
  const [todayExp,   setTodayExp]   = useState("");
  const [expNote,    setExpNote]    = useState("");
  const [addNotes,   setAddNotes]   = useState("");

  const showToast = (msg, type = "error") => {
    toast.dismiss();
    type === "success" ? toast.success(msg) : toast.error(msg);
  };

  /* ── Auth ── */
  useEffect(() => {
    const sid   = localStorage.getItem("shopId");
    const token = localStorage.getItem("tokenString");
    const sName = localStorage.getItem("shopName");
    if (sid && token && sName) { setShopId(sid); setToken(token); setShopName(sName); }
    else { showToast("Please log in first!", "error"); navigate("/signin"); }
  }, [navigate]);

  /* ══════════════════════════════════════════════
     NORMAL MODE logic (unchanged from Stock.js)
  ══════════════════════════════════════════════ */
  const checkStockData = async (d = date) => {
    try {
      const res = await apiClient.post(
        "/Liquor/GetCheckStock",
        { shopId, typeId: "1", stockDate: d || todayISO }
      );
      setApiConditionMet(res.data?.data === true ? false : true);
    } catch { setApiConditionMet(true); }
  };
  useEffect(() => { if (shopId && mode === "normal") checkStockData(date); }, [shopId, date, mode]);

  const fetchStock = async () => {
    if (!shopId || !tokenString) return;
    setLoading(true);
    try {
      const d        = date || todayISO;
      const nextDate = new Date(d);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextISO  = `${nextDate.getFullYear()}-${pad(nextDate.getMonth()+1)}-${pad(nextDate.getDate())}`;
      const res = await apiClient.post(
        "/Liquor/GetProductsForReverseEntry",
        { shopId, typeId: "1", baseDate: nextISO, entryDate: d }
      );
      const data = res.data.data || [];
      const rawMapped = data.map(p => ({
        id: p.id,
        cat: p.catName || p.category || p.categoryName || p.typeName || "अंग्रेजी",
        pName: p.pName, packing: p.packing,
        mrp: parseFloat(p.mrp) || 0,
        openingStock: parseFloat(p.openingStock) || 0,
        receivedQty:  parseFloat(p.receivedQty)  || 0,
        transferQty:  parseFloat(p.transferQty)  || 0,
        closing: "",
      }));
      // Re-sort by the authoritative shop+type sort-order map so this screen agrees
      // with every other parcha rendering surface on the same product sequence.
      const sortMap = await fetchSortOrderMap(shopId, "1");
      const mapped = applySortOrder(rawMapped, sortMap);
      setRows(mapped);
      const initSel = {}; const seen = new Set();
      mapped.forEach(r => { if (!seen.has(r.pName)) { seen.add(r.pName); initSel[r.pName] = r.id; } });
      setSelectedVars(initSel);
    } catch (err) { showToast("Error fetching stock data", "error"); console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (shopId && tokenString && shopName && mode === "normal") fetchStock();
  }, [shopId, tokenString, shopName, date, mode]);

  const handleClosing = (id, val) => {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (val === "") return { ...r, closing: "" };
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) return r;
      const max = r.openingStock + r.receivedQty - r.transferQty;
      if (n > max) { showToast(`Closing cannot exceed ${max}`, "error"); return r; }
      return { ...r, closing: val };
    }));
  };

  const fetchTransactionData = async (rowId, type) => {
    try {
      const res = await apiClient.post(
        "/Liquor/GetDailyTransaction",
        { shopId, stockDate: date || todayISO }
      );
      if (res.data?.code === 200) {
        if (type === "received")
          setPopupData1(res.data.data?.trnReceived?.filter(i => i.productId === rowId) || []);
        else
          setPopupData2(res.data.data?.trnTransfer?.filter(i => i.productId === rowId) || []);
      }
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    const stocks = rows.filter(r => r.closing !== "").map(r => {
      const cl = parseFloat(r.closing);
      const tot = r.openingStock + r.receivedQty - r.transferQty;
      const sl  = Math.max(0, tot - cl);
      return {
        productId: r.id, categoryName: r.cat,
        openingStock:  r.openingStock,
        receivedStock: r.receivedQty, transferStock: r.transferQty,
        currentStock: cl, soldStock: sl, totalAmount: sl * r.mrp,
        shopId, stockDate: date || todayISO,
      };
    });
    if (!stocks.length) { showToast("Please enter closing stock for at least one product.", "error"); return; }
    const txnDate = date || todayISO;
    const transactions = [
      ...(parseFloat(phonePe) > 0 ? [{ typeId:1, shopId, amount:parseFloat(phonePe), date:txnDate, name:"", mode:"ONLINE" }] : []),
    ];
    const expenses = [
      ...(parseFloat(commission) > 0 ? [{ shopId, expenseId: 13, expenseAmount: parseFloat(commission), userId, remark: "English Commission", expenseDate: txnDate }] : []),
      ...(parseFloat(todayExp)  > 0 ? [{ shopId, expenseId: 77, expenseAmount: parseFloat(todayExp),  userId, remark: expNote || "Daily Expense", expenseDate: txnDate }] : []),
    ];
    try {
      setLoading(true);
      const res = await apiClient.post("/Liquor/SaveStockComplete", { stocks, transactions, expenses });
      if (res.status === 200) {
        if (res.data?.data?.includes("already exist")) {
          showToast(res.data.data, "error"); return;
        }
        if (res.data?.data?.includes("Negative stock")) {
          showToast("Negative stock detected!", "error"); return;
        }
        showToast("Stock report submitted successfully!", "success");
        await checkStockData(date); fetchStock();
      }
    } catch (err) { showToast(getApiError(err, "Error submitting stock report!"), "error"); }
    finally { setLoading(false); }
  };

  /* Normal helpers */
  const rowTotal = r => r.openingStock + r.receivedQty - r.transferQty;
  const rowSold  = r => { const cl = parseFloat(r.closing); return isNaN(cl) ? 0 : Math.max(0, rowTotal(r) - cl); };
  const rowValue = r => rowSold(r) * r.mrp;
  const filtered = rows.filter(r => (activeCat === "ALL" || r.cat === activeCat) && r.pName.toLowerCase().includes(search.toLowerCase()));
  const uniqueCats  = [...new Set(rows.map(r => r.cat).filter(Boolean))];
  const filteredSegments = buildCategorySegments(filtered);
  const totalSold   = rows.reduce((s, r) => s + rowSold(r), 0);
  const totalValue  = rows.reduce((s, r) => s + rowValue(r), 0);
  const cashColl    = Math.max(0, totalValue - (parseFloat(commission)||0) - (parseFloat(phonePe)||0) - (parseFloat(todayExp)||0));
  const filledCount = rows.filter(r => r.closing !== "").length;
  const displayDate = new Date(date || todayISO).toLocaleDateString("en-GB");
  const buildGroups = catRows => {
    const order = []; const map = new Map();
    catRows.forEach(r => { if (!map.has(r.pName)) { map.set(r.pName, []); order.push(r.pName); } map.get(r.pName).push(r); });
    return order.map(name => ({ name, variants: map.get(name) }));
  };
  const selectVariant = (pName, id) => setSelectedVars(prev => ({ ...prev, [pName]: id }));

  /* ══════════════════════════════════════════════
     REVERSE MODE logic
  ══════════════════════════════════════════════ */

  /* Load base-date openings + reverse-date products in one pass */
  const loadReverseData = async (bd, rd) => {
    if (!shopId || !tokenString) return;
    if (bd <= rd) { showToast("Base date must be after reverse entry date.", "error"); return; }
    setRevLoading(true);
    setRevApiConditionMet(null);
    try {
      /* Step 0: check if revDate is already submitted */
      const chk = await apiClient.post(
        "/Liquor/GetCheckStock",
        { shopId, typeId: "1", stockDate: rd }
      );
      if (chk.data?.data === true) {
        setRevApiConditionMet(false); // already submitted
        setRevRows([]);
        setRevLoading(false);
        return;
      }
      setRevApiConditionMet(true);

      /* Single call — new SP returns all products with auto-closing embedded */
      const revRes = await apiClient.post(
        "/Liquor/GetProductsForReverseEntry",
        { shopId, typeId: "1", baseDate: bd, entryDate: rd }
      );
      const data = revRes.data.data || [];
      const mapped = data.map(p => ({
        id: p.id,
        cat: p.catName || p.category || p.categoryName || p.typeName || "अंग्रेजी",
        pName: p.pName, packing: p.packing,
        mrp: parseFloat(p.mrp) || 0,
        closing:  parseFloat(p.openingStock) || 0,  // auto-closing from base date
        opening:  "",
        received: parseFloat(p.receivedQty) > 0 ? String(parseFloat(p.receivedQty)) : "",
        transfer: parseFloat(p.transferQty) > 0 ? String(parseFloat(p.transferQty)) : "",
        breakage: "",
      }));
      setRevRows(mapped);
      const initSel = {}; const seen = new Set();
      mapped.forEach(r => { if (!seen.has(r.pName)) { seen.add(r.pName); initSel[r.pName] = r.id; } });
      setRevSelVars(initSel);
    } catch (err) {
      showToast("Error loading reverse data", "error"); console.error(err);
    } finally { setRevLoading(false); }
  };

  useEffect(() => {
    if (mode === "reverse" && shopId && tokenString && shopName) loadReverseData(baseDate, revDate);
  }, [mode, shopId, tokenString, shopName, baseDate, revDate]);

  const handleBaseDateChange = bd => {
    setBaseDate(bd);
    setRevDate(prevDay(bd));
  };

  const handleRevInput = (id, field, val) =>
    setRevRows(prev => prev.map(r => r.id !== id ? r : { ...r, [field]: val }));

  const selectRevVariant = (pName, id) => setRevSelVars(prev => ({ ...prev, [pName]: id }));

  /* Reverse formula — ONLY used in reverse mode */
  const revSale = r => {
    const op = parseFloat(r.opening)  || 0;
    const rc = parseFloat(r.received) || 0;
    const cl = parseFloat(r.closing)  || 0;
    const tr = parseFloat(r.transfer) || 0;
    const br = parseFloat(r.breakage) || 0;
    return (op + rc) - (cl + tr + br);
  };
  const revValue     = r => Math.max(0, revSale(r)) * r.mrp;
  const revMismatch  = r => r.opening !== "" && revSale(r) < 0;

  const revFilledCount   = revRows.filter(r => r.opening !== "").length;
  const revTotalSale     = revRows.reduce((s, r) => s + Math.max(0, revSale(r)), 0);
  const revTotalValue    = revRows.reduce((s, r) => s + revValue(r), 0);
  const revHasMismatch   = revRows.some(r => revMismatch(r));
  const revFiltered      = revRows.filter(r => r.pName.toLowerCase().includes(revSearch.toLowerCase()));
  const revUniqueCats    = [...new Set(revFiltered.map(r => r.cat).filter(Boolean))];
  const revDisplayDate   = new Date(revDate || todayISO).toLocaleDateString("en-GB");
  const baseDateDisplay  = new Date(baseDate || todayISO).toLocaleDateString("en-GB");

  const buildRevGroups = catRows => {
    const order = []; const map = new Map();
    catRows.forEach(r => { if (!map.has(r.pName)) { map.set(r.pName, []); order.push(r.pName); } map.get(r.pName).push(r); });
    return order.map(name => ({ name, variants: map.get(name) }));
  };

  const handleRevSubmit = async () => {
    if (revHasMismatch) {
      showToast("Stock mismatch detected! Calculated sale is negative for some products.", "error"); return;
    }
    const payload = revRows.filter(r => r.opening !== "").map(r => ({
      productId:     r.id,
      categoryName:  r.cat,
      openingStock:  parseFloat(r.opening) || 0,
      receivedStock: parseFloat(r.received) || 0,
      transferStock: parseFloat(r.transfer) || 0,
      breakageQty:   parseFloat(r.breakage) || 0,
      currentStock:  parseFloat(r.closing),
      soldStock:     Math.max(0, revSale(r)),
      totalAmount:   revValue(r),
      shopId,
      stockDate: revDate,
    }));
    if (!payload.length) { showToast("Please enter opening stock for at least one product.", "error"); return; }
    try {
      setRevLoading(true);
      const res = await apiClient.post("/Liquor/SaveReverseProductDataWithDate", payload);
      if (res.status === 200) {
        if (res.data?.data === "Negative stock detected after transaction.")
          showToast("Negative stock detected!", "error");
        else {
          showToast("Reverse entry saved successfully!", "success");
          /* ── Auto-chain: advance one day backward ── */
          const nextBase = revDate;
          const nextRev  = prevDay(revDate);
          setBaseDate(nextBase);
          setRevDate(nextRev);
        }
      }
    } catch (err) { showToast(getApiError(err, "Error saving reverse entry!"), "error"); }
    finally { setRevLoading(false); }
  };

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <div style={{ background:"#fff", minHeight:"100%", fontFamily:"'Inter',-apple-system,sans-serif" }}>
      <ToastContainer />

      {/* PAGE META */}
      <div style={{ padding:"10px 28px 8px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:0 }}>
          <span style={{ fontSize:12, color:MUT }}>
            {mode === "normal" ? `Date: ${displayDate}` : `Entry: ${revDisplayDate}  |  Base: ${baseDateDisplay}`}
          </span>
          <span style={{ fontSize:12, color:MUT }}>Shop: {shopName} &nbsp;|&nbsp; Time: {timeStr}</span>
        </div>
        <hr style={{ border:"none", borderTop:`1.5px solid ${BDR}`, margin:"10px 0 0" }}/>
      </div>

      {/* TOOLBAR */}
      <div style={{ padding:"10px 28px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap",
        background:"#FAFAFA", borderTop:`1px solid ${BDR}`, borderBottom:`1px solid ${BDR}` }}>

        {/* Mode Toggle */}
        <div style={{ display:"flex", gap:0, borderRadius:8, border:`1.5px solid ${BDR}`, overflow:"hidden" }}>
          {[["normal","📝 Normal Entry"],["reverse","⏪ Reverse Historical"]].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer",
              border:"none", borderRight: m==="normal" ? `1px solid ${BDR}` : "none",
              background: mode===m ? P : "#fff",
              color: mode===m ? "#fff" : MUT,
              transition:"all 0.15s",
            }}>{label}</button>
          ))}
        </div>

        {mode === "normal" ? (
          <>
            {/* Normal date picker */}
            <input type="date" value={date} max={todayISO}
              onChange={e => { setDate(e.target.value); setActiveCat("ALL"); setSearch(""); setRows([]); setApiConditionMet(null); }}
              style={{ border:`1.5px solid ${BDR}`, borderRadius:7, padding:"7px 10px",
                fontSize:13, outline:"none", background:"#fff", color:"#111827", cursor:"pointer" }}/>

            {/* Search */}
            <div style={{ position:"relative", flex:"1 1 180px", minWidth:140 }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:MUT, fontSize:14 }}>🔍</span>
              <input type="text" placeholder="Search product…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width:"100%", paddingLeft:32, padding:"8px 12px 8px 32px",
                  border:`1.5px solid ${BDR}`, borderRadius:7, fontSize:13, outline:"none", background:"#fff" }}/>
            </div>

            {/* Category tabs */}
            {uniqueCats.length > 0 && (
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {["ALL", ...uniqueCats].map(cat => (
                  <button key={cat} onClick={() => setActiveCat(cat)} style={{
                    padding:"5px 13px", borderRadius:20, fontSize:12, fontWeight:700,
                    cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s",
                    background: activeCat===cat ? P : "#fff", color: activeCat===cat ? "#fff" : MUT,
                    border: `1.5px solid ${activeCat===cat ? P : BDR}`,
                  }}>{cat}</button>
                ))}
              </div>
            )}
            <div style={{ flex:1 }}/>
            <button onClick={() => setPreview(true)} disabled={filledCount === 0} style={{
              display:"flex", alignItems:"center", gap:6,
              background: filledCount===0 ? "#F3F4F6" : "#FDF4F8",
              color: filledCount===0 ? MUT : P,
              border:`1.5px solid ${filledCount===0 ? BDR : P+"44"}`,
              borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700,
              cursor: filledCount===0 ? "not-allowed" : "pointer", whiteSpace:"nowrap",
            }}>👁 Preview</button>
            <button onClick={handleSubmit} disabled={loading} style={{
              display:"flex", alignItems:"center", gap:6,
              background: loading ? "#9CA3AF" : `linear-gradient(135deg,${PDK},${P})`,
              color:"#fff", border:"none", borderRadius:8, padding:"8px 20px", fontSize:13, fontWeight:700,
              cursor: loading ? "not-allowed" : "pointer", boxShadow:`0 4px 12px ${P}44`, whiteSpace:"nowrap",
            }}>
              {loading ? <><FaSpinner style={{ animation:"spin 1s linear infinite" }}/> Submitting…</> : "✅ Submit Report"}
            </button>
          </>
        ) : (
          <>
            {/* Reverse mode date pickers */}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, fontWeight:700, color:MUT }}>Entry Date:</span>
              <input type="date" value={revDate}
                max={prevDay(baseDate)}
                onChange={e => setRevDate(e.target.value)}
                style={{ border:`1.5px solid ${ORG}44`, borderRadius:7, padding:"7px 10px",
                  fontSize:13, outline:"none", background:"#FFF7ED", color:"#C2410C", cursor:"pointer" }}/>
            </div>
            <span style={{ fontSize:16, color:MUT }}>→</span>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, fontWeight:700, color:MUT }}>Base Date:</span>
              <input type="date" value={baseDate} max={todayISO}
                onChange={e => handleBaseDateChange(e.target.value)}
                style={{ border:`1.5px solid ${P}44`, borderRadius:7, padding:"7px 10px",
                  fontSize:13, outline:"none", background:"#FDF4F8", color:P, cursor:"pointer" }}/>
            </div>

            {/* Info chip */}
            <div style={{ fontSize:11, color:MUT, background:"#F0FDF4", border:`1px solid #86EFAC`,
              borderRadius:6, padding:"5px 10px", whiteSpace:"nowrap" }}>
              🔒 Auto-closing = {baseDateDisplay} opening
            </div>

            {/* Search */}
            <div style={{ position:"relative", flex:"1 1 180px", minWidth:140 }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:MUT, fontSize:14 }}>🔍</span>
              <input type="text" placeholder="Search product…" value={revSearch}
                onChange={e => setRevSearch(e.target.value)}
                style={{ width:"100%", padding:"8px 12px 8px 32px",
                  border:`1.5px solid ${BDR}`, borderRadius:7, fontSize:13, outline:"none", background:"#fff" }}/>
            </div>

            <div style={{ flex:1 }}/>

            {revApiConditionMet === null && (
              <span style={{ fontSize:12, color:MUT }}>
                <FaSpinner style={{ animation:"spin 1s linear infinite", marginRight:5 }}/>Checking…
              </span>
            )}

            {revApiConditionMet === false && (
              <div style={{ fontSize:12, fontWeight:700, color:"#059669", background:"#ECFDF5",
                border:`1px solid #6EE7B7`, borderRadius:6, padding:"5px 12px", whiteSpace:"nowrap" }}>
                ✅ पर्चा जमा हो चुका है
              </div>
            )}

            {revApiConditionMet === true && (<>
              {revHasMismatch && (
                <div style={{ fontSize:12, fontWeight:700, color:RED, background:"#FEF2F2",
                  border:`1px solid #FCA5A5`, borderRadius:6, padding:"5px 10px", whiteSpace:"nowrap" }}>
                  ⚠ Stock mismatch detected
                </div>
              )}

              <button onClick={() => setRevPreview(true)} disabled={revFilledCount === 0} style={{
                display:"flex", alignItems:"center", gap:6,
                background: revFilledCount===0 ? "#F3F4F6" : "#FDF4F8",
                color: revFilledCount===0 ? MUT : P,
                border:`1.5px solid ${revFilledCount===0 ? BDR : P+"44"}`,
                borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700,
                cursor: revFilledCount===0 ? "not-allowed" : "pointer", whiteSpace:"nowrap",
              }}>👁 Preview</button>

              <button onClick={handleRevSubmit} disabled={revLoading || revHasMismatch} style={{
                display:"flex", alignItems:"center", gap:6,
                background: (revLoading || revHasMismatch) ? "#9CA3AF" : `linear-gradient(135deg,${PDK},${P})`,
                color:"#fff", border:"none", borderRadius:8, padding:"8px 20px", fontSize:13, fontWeight:700,
                cursor: (revLoading || revHasMismatch) ? "not-allowed" : "pointer",
                boxShadow:`0 4px 12px ${P}44`, whiteSpace:"nowrap",
              }}>
                {revLoading ? <><FaSpinner style={{ animation:"spin 1s linear infinite" }}/> Saving…</> : "✅ Save Reverse Entry"}
              </button>
            </>)}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════
          NORMAL MODE CONTENT
      ═══════════════════════════════════════════ */}
      {mode === "normal" && (
        <>
          {apiConditionMet === null && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", padding:"60px 0", gap:14, color:MUT }}>
              <FaSpinner style={{ fontSize:28, animation:"spin 1s linear infinite" }}/>
              <span style={{ fontSize:13, fontWeight:500 }}>Loading stock data…</span>
            </div>
          )}
          {apiConditionMet === false && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", padding:"52px 24px", gap:12, textAlign:"center" }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:"#ECFDF5",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:28 }}>✅</div>
              <div style={{ fontWeight:800, fontSize:17, color:"#111827" }}>पर्चा जमा हो चुका है</div>
              <div style={{ fontSize:13, color:MUT }}>Stock report for {displayDate} has already been submitted.</div>
            </div>
          )}
          {apiConditionMet === true && (
            <div style={{ padding:"16px 28px 0" }}>
              <div style={{ border:`1px solid ${BDR}`, borderRadius:12,
                overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
                {filteredSegments.map((seg, ci) => {
                  const cat = seg.cat;
                  const catRows = seg.rows;
                  const groups   = buildGroups(catRows);
                  const subSold  = catRows.reduce((s,r) => s + rowSold(r), 0);
                  const subValue = catRows.reduce((s,r) => s + rowValue(r), 0);
                  const showCat  = cat !== "";
                  return (
                    <div key={`${cat||"all"}-${ci}`} style={{ borderBottom: ci<filteredSegments.length-1 ? `1px solid ${BDR}` : "none" }}>
                      <div style={{ display:"flex" }}>
                        {showCat && (
                          <div style={{ width:34, flexShrink:0, display:"flex", alignItems:"center",
                            justifyContent:"center", borderRight:`1px solid ${BDR}`, background:"#fff" }}>
                            <span style={{ writingMode:"vertical-rl", transform:"rotate(180deg)",
                              fontSize:10, fontWeight:800, color:P,
                              letterSpacing:2.5, textTransform:"uppercase", userSelect:"none" }}>{cat}</span>
                          </div>
                        )}
                        <div style={{ flex:1, overflowX:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:860 }}>
                            <thead>
                              <tr style={{ background:P }}>
                                {["Product","Variant","Rate","Opening","Received","Transfer","Total","Closing","Sold","Value"].map(h => (
                                  <th key={h} style={{ padding:"9px 12px", color:"#fff", fontWeight:600, fontSize:11,
                                    textAlign:["Product","Variant"].includes(h)?"left":"right",
                                    whiteSpace:"nowrap", borderRight:"1px solid rgba(255,255,255,0.10)" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {groups.map(({ name, variants }, gi) => {
                                const selId  = selectedVars[name] ?? variants[0].id;
                                const r      = variants.find(v => v.id === selId) || variants[0];
                                const tot    = rowTotal(r);
                                const sl     = rowSold(r);
                                const vl     = rowValue(r);
                                const filled = r.closing !== "";
                                const filledVars = variants.filter(v => v.closing !== "").length;
                                return (
                                  <tr key={name} style={{ background: filled ? "#FDF9FB" : "#fff",
                                    borderTop: gi > 0 ? "2px solid #E5E7EB" : undefined }}>
                                    <td style={{ padding:"10px 14px", fontWeight:700, fontSize:13, color:"#111827",
                                      borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                      verticalAlign:"middle", background:"#FEFBFD", minWidth:160 }}>
                                      <div>{name}</div>
                                      {variants.length > 1 && (
                                        <div style={{ marginTop:5, fontSize:10, fontWeight:600,
                                          color: filledVars===variants.length ? GRN : MUT }}>
                                          {filledVars}/{variants.length} filled
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding:"8px 12px", verticalAlign:"middle",
                                      borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>
                                      <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                                        {variants.map(v => {
                                          const isActive = v.id === selId;
                                          const isDone   = v.closing !== "";
                                          return (
                                            <button key={v.id} onClick={() => selectVariant(name, v.id)}
                                              style={{ padding:"3px 10px", borderRadius:20, fontSize:12,
                                                fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s",
                                                background: isActive ? P : isDone ? `${P}15` : "#F3F4F6",
                                                color: isActive ? "#fff" : isDone ? P : "#374151",
                                                border: `1.5px solid ${isActive ? P : isDone ? `${P}44` : BDR}`,
                                                boxShadow: isActive ? `0 2px 6px ${P}44` : "none",
                                              }}>
                                              {v.packing}{isDone && !isActive ? " ✓" : ""}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </td>
                                    <NC>{fmt(r.mrp)}</NC>
                                    <NC>{r.openingStock}</NC>
                                    <td style={{ padding:"9px 10px", textAlign:"right", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`, verticalAlign:"middle" }}>
                                      <button data-bs-toggle="modal" data-bs-target="#recModal"
                                        onClick={() => fetchTransactionData(r.id, "received")}
                                        style={{ background: r.receivedQty>0 ? "#ECFDF5" : "#F9FAFB",
                                          color: r.receivedQty>0 ? "#065F46" : MUT,
                                          border:`1px solid ${r.receivedQty>0 ? "#6EE7B7" : BDR}`,
                                          borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                                        {r.receivedQty > 0 ? `⬇ ${r.receivedQty}` : "—"}
                                      </button>
                                    </td>
                                    <td style={{ padding:"9px 10px", textAlign:"right", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`, verticalAlign:"middle" }}>
                                      <button data-bs-toggle="modal" data-bs-target="#trnModal"
                                        onClick={() => fetchTransactionData(r.id, "transfer")}
                                        style={{ background: r.transferQty>0 ? "#FFF7ED" : "#F9FAFB",
                                          color: r.transferQty>0 ? "#C2410C" : MUT,
                                          border:`1px solid ${r.transferQty>0 ? "#FED7AA" : BDR}`,
                                          borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                                        {r.transferQty > 0 ? `⬆ ${r.transferQty}` : "—"}
                                      </button>
                                    </td>
                                    <NC bold>{tot}</NC>
                                    <td style={{ padding:"5px 8px", textAlign:"center", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`, verticalAlign:"middle" }}>
                                      <input type="number" min="0" value={r.closing}
                                        onChange={e => handleClosing(r.id, e.target.value)}
                                        placeholder={String(tot)}
                                        style={{ width:70, textAlign:"center",
                                          border:`1.5px solid ${filled ? P : BDR}`, borderRadius:6, padding:"5px 6px",
                                          fontSize:13, fontWeight:filled?700:400,
                                          color:filled?P:"#374151", background:filled?"#FDF4F8":"#fff", outline:"none" }}/>
                                    </td>
                                    <NC color={sl>0?"#111827":MUT} bold={sl>0}>{sl}</NC>
                                    <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:600,
                                      color:vl>0?"#111827":MUT, borderBottom:`1px solid ${BDR}` }}>{fmt(vl)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          {showCat && (
                            <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center",
                              padding:"7px 16px", background:"#FDF4F8", borderTop:`1px solid ${BDR}`, gap:20 }}>
                              <span style={{ fontSize:12, color:MUT }}>Sold: <b style={{ color:"#111827" }}>{subSold}</b></span>
                              <span style={{ fontSize:12, fontWeight:700, color:MUT }}>{cat} Subtotal:</span>
                              <span style={{ fontSize:14, fontWeight:800, color:P, minWidth:80, textAlign:"right" }}>{fmt(subValue)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TOTAL SALE AMOUNT CARD */}
          {apiConditionMet === true && (
            <div className="parcha-sale-amt-card" style={{margin:"18px 0 0"}}>
              <div className="parcha-sale-amt-label">Total Sale Amount</div>
              <div className="parcha-sale-amt-value">{fmt(totalValue)}</div>
            </div>
          )}

          {/* DAILY CLOSING SUMMARY */}
          {apiConditionMet === true && (
            <div className="parcha-closing-summary" style={{margin:"18px 0 0"}}>
              <div className="pcs-title">Daily Closing Summary</div>
              <div className="pcs-body">
                <div className="pcs-row3">
                  <div className="pcs-field">
                    <div className="pcs-label">English Commission</div>
                    <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                      <input type="number" className="pcs-inp" placeholder="0" value={commission} onChange={e=>setCommission(e.target.value)}/></div>
                  </div>
                  <div className="pcs-field">
                    <div className="pcs-label">PhonePe Collection</div>
                    <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                      <input type="number" className="pcs-inp" placeholder="0" value={phonePe} onChange={e=>setPhonePe(e.target.value)}/></div>
                  </div>
                  <div className="pcs-field">
                    <div className="pcs-label">Net Cash In Hand</div>
                    <div className="pcs-inp-wrap pcs-readonly"><span className="pcs-prefix">₹</span>
                      <span className="pcs-inp pcs-auto-val">{fmt(cashColl)}</span></div>
                  </div>
                </div>
                <div className="pcs-row2">
                  <div className="pcs-field">
                    <div className="pcs-label">Today's Expense</div>
                    <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                      <input type="number" className="pcs-inp" placeholder="0" value={todayExp} onChange={e=>setTodayExp(e.target.value)}/></div>
                  </div>
                  <div className="pcs-field">
                    <div className="pcs-label">Expense Note</div>
                    <div className="pcs-inp-wrap">
                      <input type="text" className="pcs-inp" placeholder="Brief description..." value={expNote} onChange={e=>setExpNote(e.target.value)}/></div>
                  </div>
                </div>
                <div className="pcs-row1">
                  <div className="pcs-field">
                    <div className="pcs-label">Additional Notes</div>
                    <textarea className="pcs-textarea" placeholder="Any additional notes or remarks..."
                      value={addNotes} onChange={e=>setAddNotes(e.target.value)}/>
                  </div>
                </div>
                <div className="pcs-totals">
                  <div className="pcs-tot-box blue"><div className="pcs-tot-lbl">Online (PhonePe + Commission)</div><div className="pcs-tot-val">{fmt((parseFloat(phonePe)||0)+(parseFloat(commission)||0))}</div></div>
                  <div className="pcs-tot-box amber"><div className="pcs-tot-lbl">Today's Expense</div><div className="pcs-tot-val">{fmt(parseFloat(todayExp)||0)}</div></div>
                  <div className="pcs-tot-box green"><div className="pcs-tot-lbl">Net Cash In Hand</div><div className="pcs-tot-val">{fmt(cashColl)}</div></div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════
          REVERSE MODE CONTENT
      ═══════════════════════════════════════════ */}
      {mode === "reverse" && (
        <div style={{ padding:"16px 28px 0" }}>

          {/* Already submitted */}
          {revApiConditionMet === null && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", padding:"60px 0", gap:14, color:MUT }}>
              <FaSpinner style={{ fontSize:28, animation:"spin 1s linear infinite" }}/>
              <span style={{ fontSize:13, fontWeight:500 }}>Checking entry status…</span>
            </div>
          )}
          {revApiConditionMet === false && (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", padding:"52px 24px", gap:12, textAlign:"center" }}>
              <div style={{ width:64, height:64, borderRadius:"50%", background:"#ECFDF5",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:28 }}>✅</div>
              <div style={{ fontWeight:800, fontSize:17, color:"#111827" }}>पर्चा जमा हो चुका है</div>
              <div style={{ fontSize:13, color:MUT }}>
                Reverse entry for {new Date(revDate).toLocaleDateString("en-GB")} has already been submitted.
              </div>
            </div>
          )}

          {/* Info banner */}
          {revApiConditionMet === true && (<>
          <div style={{ marginBottom:14, padding:"12px 18px", background:"#EFF6FF",
            border:`1px solid #BFDBFE`, borderRadius:10, display:"flex", gap:16, flexWrap:"wrap" }}>
            <div>
              <span style={{ fontSize:11, fontWeight:700, color:"#1D4ED8" }}>📅 BASE DATE</span>
              <div style={{ fontSize:15, fontWeight:800, color:"#1E3A8A" }}>{baseDateDisplay}</div>
              <div style={{ fontSize:11, color:MUT }}>Opening of this date = auto-closing for entry date</div>
            </div>
            <div style={{ borderLeft:`1px solid #BFDBFE`, paddingLeft:16 }}>
              <span style={{ fontSize:11, fontWeight:700, color:ORG }}>📝 ENTRY DATE</span>
              <div style={{ fontSize:15, fontWeight:800, color:"#C2410C" }}>{revDisplayDate}</div>
              <div style={{ fontSize:11, color:MUT }}>Entering historical stock for this date</div>
            </div>
            <div style={{ borderLeft:`1px solid #BFDBFE`, paddingLeft:16 }}>
              <span style={{ fontSize:11, fontWeight:700, color:P }}>📐 FORMULA</span>
              <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginTop:2 }}>
                Sale = (Opening + Received) − (Closing + Transfer + Breakage)
              </div>
              <div style={{ fontSize:11, color:MUT }}>Closing is auto-set from base date opening</div>
            </div>
          </div>

          {revLoading ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", padding:"60px 0", gap:14, color:MUT }}>
              <FaSpinner style={{ fontSize:28, animation:"spin 1s linear infinite" }}/>
              <span style={{ fontSize:13, fontWeight:500 }}>Loading reverse data…</span>
            </div>
          ) : revRows.length === 0 ? (
            <EmptyState message="No products found for selected date range" />
          ) : (
            <div style={{ border:`1px solid ${BDR}`, borderRadius:12,
              overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>

              {(() => {
                const revDisplayCats = revUniqueCats.length > 0 ? revUniqueCats : [""];
                return revDisplayCats.map((cat, ci) => {
                  const catRows  = cat === "" ? revFiltered : revFiltered.filter(r => r.cat === cat);
                  if (!catRows.length) return null;
                  const groups   = buildRevGroups(catRows);
                  const subSale  = catRows.reduce((s,r) => s + Math.max(0, revSale(r)), 0);
                  const subValue = catRows.reduce((s,r) => s + revValue(r), 0);
                  const showCat  = cat !== "";
                  return (
                    <div key={cat||"all"} style={{ borderBottom: ci<revDisplayCats.length-1 ? `1px solid ${BDR}` : "none" }}>
                      <div style={{ display:"flex" }}>
                        {showCat && (
                          <div style={{ width:34, flexShrink:0, display:"flex", alignItems:"center",
                            justifyContent:"center", borderRight:`1px solid ${BDR}`, background:"#fff" }}>
                            <span style={{ writingMode:"vertical-rl", transform:"rotate(180deg)",
                              fontSize:10, fontWeight:800, color:P,
                              letterSpacing:2.5, textTransform:"uppercase", userSelect:"none" }}>{cat}</span>
                          </div>
                        )}
                        <div style={{ flex:1, overflowX:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13, minWidth:1000 }}>
                            <thead>
                              <tr style={{ background:P }}>
                                {["Product","Variant","Rate","Auto-Closing 🔒","Opening","Received","Transfer","Breakage","Auto Sale","Auto Value"].map(h => (
                                  <th key={h} style={{ padding:"9px 12px", color:"#fff", fontWeight:600, fontSize:11,
                                    textAlign:["Product","Variant"].includes(h)?"left":"right",
                                    whiteSpace:"nowrap", borderRight:"1px solid rgba(255,255,255,0.10)" }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {groups.map(({ name, variants }, gi) => {
                                const selId  = revSelVars[name] ?? variants[0].id;
                                const r      = variants.find(v => v.id === selId) || variants[0];
                                const sale   = revSale(r);
                                const val    = revValue(r);
                                const filled = r.opening !== "";
                                const mismatch = revMismatch(r);
                                const filledVars = variants.filter(v => v.opening !== "").length;
                                return (
                                  <tr key={name} style={{
                                    background: mismatch ? "#FEF2F2" : filled ? "#FDF9FB" : "#fff",
                                    borderTop: gi > 0 ? "2px solid #E5E7EB" : undefined }}>

                                    {/* Product */}
                                    <td style={{ padding:"10px 14px", fontWeight:700, fontSize:13, color:"#111827",
                                      borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                      verticalAlign:"middle", background: mismatch ? "#FEF9F9" : "#FEFBFD", minWidth:150 }}>
                                      <div>{name}</div>
                                      {mismatch && <div style={{ fontSize:10, color:RED, fontWeight:700, marginTop:3 }}>⚠ Mismatch</div>}
                                      {variants.length > 1 && !mismatch && (
                                        <div style={{ marginTop:3, fontSize:10, fontWeight:600,
                                          color: filledVars===variants.length ? GRN : MUT }}>
                                          {filledVars}/{variants.length} filled
                                        </div>
                                      )}
                                    </td>

                                    {/* Variant chips */}
                                    <td style={{ padding:"8px 12px", verticalAlign:"middle",
                                      borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>
                                      <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                                        {variants.map(v => {
                                          const isActive = v.id === selId;
                                          const isDone   = v.opening !== "";
                                          return (
                                            <button key={v.id} onClick={() => selectRevVariant(name, v.id)}
                                              style={{ padding:"3px 10px", borderRadius:20, fontSize:12,
                                                fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", transition:"all 0.15s",
                                                background: isActive ? P : isDone ? `${P}15` : "#F3F4F6",
                                                color: isActive ? "#fff" : isDone ? P : "#374151",
                                                border: `1.5px solid ${isActive ? P : isDone ? `${P}44` : BDR}`,
                                                boxShadow: isActive ? `0 2px 6px ${P}44` : "none",
                                              }}>
                                              {v.packing}{isDone && !isActive ? " ✓" : ""}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </td>

                                    {/* Rate */}
                                    <NC>{fmt(r.mrp)}</NC>

                                    {/* Auto-Closing 🔒 — read-only from base date */}
                                    <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:700,
                                      color:"#1D4ED8", background:"#EFF6FF",
                                      borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                      verticalAlign:"middle" }}>
                                      <span title={`Auto from ${baseDateDisplay} opening`}>
                                        🔒 {r.closing}
                                      </span>
                                    </td>

                                    {/* Opening — user input */}
                                    <RevInput id={r.id} field="opening" val={r.opening} onChange={handleRevInput}
                                      border={mismatch ? RED : filled ? GRN : BDR}/>

                                    {/* Received — user input */}
                                    <RevInput id={r.id} field="received" val={r.received} onChange={handleRevInput}/>

                                    {/* Transfer — user input */}
                                    <RevInput id={r.id} field="transfer" val={r.transfer} onChange={handleRevInput}/>

                                    {/* Breakage — user input */}
                                    <RevInput id={r.id} field="breakage" val={r.breakage} onChange={handleRevInput}/>

                                    {/* Auto Sale */}
                                    <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:700,
                                      color: mismatch ? RED : sale > 0 ? "#111827" : MUT,
                                      background: mismatch ? "#FEF2F2" : filled ? "#F0FDF4" : "#fff",
                                      borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                      verticalAlign:"middle" }}>
                                      {filled ? (mismatch ? "⚠ " + sale : sale) : "—"}
                                    </td>

                                    {/* Auto Value */}
                                    <td style={{ padding:"9px 12px", textAlign:"right", fontWeight:700,
                                      color: mismatch ? RED : val > 0 ? "#111827" : MUT,
                                      background: mismatch ? "#FEF2F2" : filled ? "#F0FDF4" : "#fff",
                                      borderBottom:`1px solid ${BDR}`,
                                      verticalAlign:"middle" }}>
                                      {filled ? fmt(val) : "—"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>

                          {showCat && (
                            <div style={{ display:"flex", justifyContent:"flex-end", alignItems:"center",
                              padding:"7px 16px", background:"#FDF4F8", borderTop:`1px solid ${BDR}`, gap:20 }}>
                              <span style={{ fontSize:12, color:MUT }}>Auto Sale: <b style={{ color:"#111827" }}>{subSale}</b></span>
                              <span style={{ fontSize:12, fontWeight:700, color:MUT }}>{cat} Subtotal:</span>
                              <span style={{ fontSize:14, fontWeight:800, color:P, minWidth:80, textAlign:"right" }}>{fmt(subValue)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Grand total */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"16px 28px", background:"#FDF4F8", borderTop:`2px solid ${P}22` }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>Grand Total Auto Sale Amount</div>
                  <div style={{ fontSize:12, color:MUT, marginTop:2 }}>Total Auto Sale Qty: {revTotalSale} units</div>
                </div>
                <div style={{ fontSize:26, fontWeight:900, color:P }}>{fmt(revTotalValue)}</div>
              </div>
            </div>
          )}
          </>)}
        </div>
      )}

      {/* ═══ NORMAL MODE PREVIEW MODAL ════════════════════════════ */}
      {preview && (
        <div style={{ position:"fixed", inset:0, zIndex:9999,
          background:"rgba(15,23,42,0.6)", backdropFilter:"blur(4px)",
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"24px 16px", overflowY:"auto" }}>
          <div style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:"min(95vw, 900px)",
            boxShadow:"0 24px 64px rgba(0,0,0,0.25)" }}>
            <div style={{ padding:"16px 24px", borderBottom:`1px solid ${BDR}`,
              display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:P }}>👁 Preview — अंग्रेजी</div>
                <div style={{ fontSize:12, color:MUT, marginTop:2 }}>Date: {displayDate} | Filled: {filledCount}/{rows.length}</div>
              </div>
              <button onClick={() => setPreview(false)} style={{ background:"#F3F4F6", border:`1px solid ${BDR}`,
                borderRadius:7, padding:"7px 16px", fontSize:12, fontWeight:600, cursor:"pointer", color:"#374151" }}>✕ Close</button>
            </div>
            <div style={{ padding:"16px 24px", overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:P }}>
                    {["Product","Vol","Rate","Opening","Received","Transfer","Total","Closing","Sold","Value"].map(h => (
                      <th key={h} style={{ padding:"8px 10px", color:"#fff", fontWeight:600, fontSize:11,
                        textAlign:["Product","Vol"].includes(h)?"left":"right",
                        borderRight:"1px solid rgba(255,255,255,0.10)", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.filter(r => r.closing !== "").map((r,i) => {
                    const tot = rowTotal(r), sl = rowSold(r), vl = rowValue(r);
                    return (
                      <tr key={r.id} style={{ background:i%2===0?"#fff":"#FAFAFA" }}>
                        <td style={{ padding:"7px 10px", fontWeight:700, borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>{r.pName}</td>
                        <td style={{ padding:"7px 10px", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>
                          <span style={{ background:"#F3F4F6", borderRadius:4, padding:"1px 6px", fontSize:10, fontWeight:600 }}>{r.packing}</span>
                        </td>
                        <SNC>{fmt(r.mrp)}</SNC>
                        <SNC>{r.openingStock}</SNC>
                        <SNC color={GRN} bold>{r.receivedQty}</SNC>
                        <SNC color={ORG} bold>{r.transferQty}</SNC>
                        <SNC bold>{tot}</SNC>
                        <td style={{ padding:"7px 10px", textAlign:"center", fontWeight:700, color:P, borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>{r.closing}</td>
                        <SNC color={sl>0?"#111827":MUT} bold={sl>0}>{sl}</SNC>
                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:600, color:vl>0?"#111827":MUT, borderBottom:`1px solid ${BDR}` }}>{fmt(vl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding:"14px 24px", borderTop:`1px solid ${BDR}`,
              display:"flex", justifyContent:"flex-end", gap:10, background:"#FAFAFA" }}>
              <button onClick={() => setPreview(false)} style={{ background:"#F3F4F6", color:"#374151",
                border:`1px solid ${BDR}`, borderRadius:8, padding:"9px 22px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Close</button>
              <button onClick={() => { setPreview(false); handleSubmit(); }} style={{
                background:`linear-gradient(135deg,${PDK},${P})`, color:"#fff", border:"none",
                borderRadius:8, padding:"9px 24px", fontSize:13, fontWeight:700, cursor:"pointer",
                boxShadow:`0 4px 12px ${P}44` }}>✅ Confirm & Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ REVERSE MODE PREVIEW MODAL ════════════════════════════ */}
      {revPreview && (
        <div style={{ position:"fixed", inset:0, zIndex:9999,
          background:"rgba(15,23,42,0.6)", backdropFilter:"blur(4px)",
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"24px 16px", overflowY:"auto" }}>
          <div style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:"min(95vw, 1000px)",
            boxShadow:"0 24px 64px rgba(0,0,0,0.25)" }}>
            <div style={{ padding:"16px 24px", borderBottom:`1px solid ${BDR}`,
              display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:P }}>👁 Reverse Entry Preview</div>
                <div style={{ fontSize:12, color:MUT, marginTop:2 }}>
                  Entry Date: {revDisplayDate} &nbsp;|&nbsp; Base: {baseDateDisplay} &nbsp;|&nbsp; Filled: {revFilledCount}/{revRows.length}
                </div>
              </div>
              <button onClick={() => setRevPreview(false)} style={{ background:"#F3F4F6", border:`1px solid ${BDR}`,
                borderRadius:7, padding:"7px 16px", fontSize:12, fontWeight:600, cursor:"pointer", color:"#374151" }}>✕ Close</button>
            </div>
            <div style={{ padding:"16px 24px", overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:P }}>
                    {["Product","Vol","Rate","Closing🔒","Opening","Received","Transfer","Breakage","Auto Sale","Auto Value"].map(h => (
                      <th key={h} style={{ padding:"8px 10px", color:"#fff", fontWeight:600, fontSize:11,
                        textAlign:["Product","Vol"].includes(h)?"left":"right",
                        borderRight:"1px solid rgba(255,255,255,0.10)", whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {revRows.filter(r => r.opening !== "").map((r,i) => {
                    const sale = revSale(r); const val = revValue(r); const err = revMismatch(r);
                    return (
                      <tr key={r.id} style={{ background: err ? "#FEF2F2" : i%2===0 ? "#fff" : "#FAFAFA" }}>
                        <td style={{ padding:"7px 10px", fontWeight:700, borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`, color: err?RED:"#111827" }}>{r.pName}</td>
                        <td style={{ padding:"7px 10px", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>
                          <span style={{ background:"#F3F4F6", borderRadius:4, padding:"1px 6px", fontSize:10, fontWeight:600 }}>{r.packing}</span>
                        </td>
                        <SNC>{fmt(r.mrp)}</SNC>
                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:700, color:"#1D4ED8", background:"#EFF6FF", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>{r.closing}</td>
                        <SNC color={GRN} bold>{r.opening||"—"}</SNC>
                        <SNC>{r.received||0}</SNC>
                        <SNC color={ORG}>{r.transfer||0}</SNC>
                        <SNC color={RED}>{r.breakage||0}</SNC>
                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:700, color:err?RED:sale>0?"#111827":MUT, borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}` }}>{err ? `⚠ ${sale}` : sale}</td>
                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:600, color:err?RED:val>0?"#111827":MUT, borderBottom:`1px solid ${BDR}` }}>{fmt(val)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background:"#FDF4F8" }}>
                    <td colSpan={8} style={{ padding:"10px 14px", fontWeight:800, color:"#111827" }}>Grand Total</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:800, color:"#111827" }}>{revTotalSale}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:900, color:P, fontSize:15 }}>{fmt(revTotalValue)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{ padding:"14px 24px", borderTop:`1px solid ${BDR}`,
              display:"flex", justifyContent:"flex-end", gap:10, background:"#FAFAFA" }}>
              <button onClick={() => setRevPreview(false)} style={{ background:"#F3F4F6", color:"#374151",
                border:`1px solid ${BDR}`, borderRadius:8, padding:"9px 22px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Close</button>
              {!revHasMismatch && (
                <button onClick={() => { setRevPreview(false); handleRevSubmit(); }} style={{
                  background:`linear-gradient(135deg,${PDK},${P})`, color:"#fff", border:"none",
                  borderRadius:8, padding:"9px 24px", fontSize:13, fontWeight:700, cursor:"pointer",
                  boxShadow:`0 4px 12px ${P}44` }}>✅ Confirm & Save</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* RECEIVED DETAIL MODAL (normal mode) */}
      <div className="modal fade" id="recModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg"><div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" style={{ color:P, fontWeight:700 }}>⬇ Received Stock Details</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"/>
          </div>
          <div className="modal-body">
            {popupData1.length > 0 ? (
              <table className="table table-bordered table-striped">
                <thead><tr><th>Product</th><th>Receive Type</th><th>Details</th><th>Qty</th></tr></thead>
                <tbody>
                  {popupData1.map((item, i) => (
                    <tr key={item.id || i}>
                      <td>{item.productName || "N/A"}</td>
                      <td>{item.receivedHead || "N/A"}</td>
                      <td>{item.receivedHead==="RSBCL"?`Invoice: ${item.invoiceNo||"N/A"}`:
                        item.receivedHeadType==="SHOP"||item.receivedHeadType==="SHOP TO SHOP"?
                        `Shop: ${item.selectedShop||"N/A"}`:`Party: ${item.receivedHeadName||"N/A"}`}</td>
                      <td style={{ fontWeight:700 }}>{item.qty || "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState message="No received stock data" />}
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button></div>
        </div></div>
      </div>

      {/* TRANSFER DETAIL MODAL (normal mode) */}
      <div className="modal fade" id="trnModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg"><div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" style={{ color:P, fontWeight:700 }}>⬆ Transfer Stock Details</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"/>
          </div>
          <div className="modal-body">
            {popupData2.length > 0 ? (
              <table className="table table-bordered table-striped">
                <thead><tr><th>Product</th><th>Transfer Head</th><th>Detail</th><th>Qty</th></tr></thead>
                <tbody>
                  {popupData2.map((item, i) => (
                    <tr key={item.id || i}>
                      <td>{item.productName || "N/A"}</td>
                      <td>{item.transferHead || "N/A"}</td>
                      <td>{item.invoiceNo || item.transferPartyName || item.transferShopName || "N/A"}</td>
                      <td style={{ fontWeight:700 }}>{item.qty || "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState message="No transfer data" />}
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button></div>
        </div></div>
      </div>

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

/* Shared cell components */
function NC({ children, color, bold }) {
  return (
    <td style={{ padding:"9px 12px", textAlign:"right", color:color||"#374151", fontWeight:bold?700:500,
      borderBottom:"1px solid #E5E7EB", borderRight:"1px solid #E5E7EB", verticalAlign:"middle" }}>{children}</td>
  );
}
function SNC({ children, top, color, bold }) {
  return (
    <td style={{ padding:"7px 10px", textAlign:"right", color:color||"#374151", fontWeight:bold?700:500,
      borderBottom:"1px solid #E5E7EB", borderRight:"1px solid #E5E7EB", borderTop:top }}>{children}</td>
  );
}
/* Reverse mode number input cell */
function RevInput({ id, field, val, onChange, border }) {
  const BDR = "#E5E7EB";
  return (
    <td style={{ padding:"5px 8px", textAlign:"center", borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`, verticalAlign:"middle" }}>
      <input type="number" min="0" value={val}
        onChange={e => onChange(id, field, e.target.value)}
        placeholder="0"
        style={{ width:66, textAlign:"center",
          border:`1.5px solid ${border||BDR}`, borderRadius:6, padding:"5px 6px",
          fontSize:13, color:"#374151", background:"#fff", outline:"none" }}/>
    </td>
  );
}
